import { zodResolver } from "@hookform/resolvers/zod";
import { Box, Grid, Stack, Typography } from "@mui/material";
import {
  type PersonalInformationFormData,
  type PersonalInformationFormDataInput,
  usePersonalInformationValidation,
} from "Components/Onboarding/shared/Hooks/usePersonalInformationValidation";
import { StepBox } from "Components/Onboarding/shared/StepBox";
import { ValuesBox } from "Components/Onboarding/shared/ValuesBox";

import { Button } from "@mui/material";
import HomeIcon from "@mui/icons-material/Home";
import { FullHeightForm } from "Components/Shared/FormStyles";
import { AddressInput } from "Components/Shared/Inputs/Form/AddressInput";
import {
  AddressUncommittedProvider,
  useAddressUncommittedTracker,
} from "Components/Shared/Inputs/Form/AddressUncommittedContext";
import { CheckboxInput } from "Components/Shared/Inputs/Form/CheckboxInput";
import { Dropdown } from "Components/Shared/Inputs/Form/Dropdown";
import { CountryDropdown } from "Components/Shared/Inputs/Form/Dropdowns/CountryDropdown";
import { FormInput } from "Components/Shared/Inputs/Form/FormInput";
import { useGenderList } from "Hooks/Options/useGenderList";
import { upsertAddressByType } from "Components/Onboarding/utils";
import {
  AddressType,
  type AddressDto,
  type AddressSuggestionDto,
  type CountryCode,
  type PartyDto,
} from "Infrastructure/Api/Api";
import { Resources, useResource } from "Translations/Resources";
import { copyAddressDtoToFormFields } from "Utils/AddressUtils";
import { PersonalIdentification } from "Utils/PersonalIdentification";
import { useEffect } from "react";
import { FormProvider, useForm, useWatch } from "react-hook-form";
import { toast } from "sonner";
import { getPersonalInformationFormData } from "Components/Onboarding/shared/PersonalInformation/utils";

const PERSONAL_RESOURCES = Resources.Onboarding.Steps.PersonalInformation;

type Props = {
  initialValues: PartyDto;
  onSave: (values: PartyDto) => void;
  shouldValidateOnMount?: boolean;
  companyHeadquartersAddress?: AddressDto;
};

export const EditValues = ({
  onSave,
  initialValues,
  shouldValidateOnMount = false,
  companyHeadquartersAddress,
}: Props) => {
  const { t } = useResource();
  const userDetailValidationSchema = usePersonalInformationValidation();
  const formMethods = useForm<
    PersonalInformationFormDataInput,
    unknown,
    PersonalInformationFormData
  >({
    mode: "onChange",
    defaultValues: getPersonalInformationFormData(initialValues),
    resolver: zodResolver(userDetailValidationSchema),
  });
  const {
    handleSubmit,
    control,
    watch,
    setValue,
    trigger,
    formState: { errors },
  } = formMethods;
  const genderList = useGenderList();
  const address = useWatch({ control, name: "address" });
  const nationality = useWatch({ control, name: "nationality" });
  const otherNationalities = useWatch({ control, name: "otherNationalities" });
  // Form-only field: it reveals the picker of a further nationality, bound to the first slot of
  // otherNationalities (the party can hold more; the form offers one). The schema requires a pick
  // while it is ticked; it is stripped from the payload in onSubmit.
  const hasOtherNationality = useWatch({
    control,
    name: "hasOtherNationality",
  });
  const { hasUncommittedAddress, uncommittedContextValue } =
    useAddressUncommittedTracker();

  useEffect(
    function validateOnMount() {
      if (shouldValidateOnMount) {
        trigger();
      }
    },
    [trigger, shouldValidateOnMount],
  );

  const personalNumber = watch("personalNumber");

  useEffect(() => {
    if (personalNumber) {
      const personInfo = PersonalIdentification.getInfo(personalNumber);

      if (!personInfo.IsValid) {
        return;
      }

      if (personInfo.Gender) {
        setValue("gender", personInfo.Gender);
      }
    }
  }, [personalNumber, setValue]);

  const handleCopyCompanyAddress = () => {
    if (!companyHeadquartersAddress) {
      return;
    }
    copyAddressDtoToFormFields(companyHeadquartersAddress, (field, value) =>
      setValue(`address.${field}`, value, { shouldDirty: true }),
    );
    trigger("address");
  };

  const handleAcceptAddressSuggestion = (suggestion: AddressSuggestionDto) => {
    copyAddressDtoToFormFields(suggestion, (field, value) =>
      setValue(`address.${field}`, value, {
        shouldDirty: true,
        shouldValidate: true,
      }),
    );
  };

  const handleClearAddress = () => {
    copyAddressDtoToFormFields({}, (field, value) =>
      setValue(`address.${field}`, value, {
        shouldDirty: true,
        shouldValidate: true,
      }),
    );
  };

  // Unticking drops the further nationality; the hidden picker must not keep sending it.
  const handleOtherNationalityChange = (isChecked: boolean) => {
    if (!isChecked) {
      setValue("otherNationalities", [], {
        shouldDirty: true,
        shouldValidate: true,
      });
    }
  };

  // Picking a further country as the primary one leaves a duplicate — clear just that slot instead
  // of making the user find the stale value. Per slot, not the whole array: setValue on the array
  // doesn't refresh the mounted pickers, and the schema drops the null on submit.
  const handleNationalityChange = (code: CountryCode | null) => {
    otherNationalities?.forEach((other, index) => {
      if (code && other === code) {
        setValue(`otherNationalities.${index}`, null, {
          shouldDirty: true,
          shouldValidate: true,
        });
      }
    });
  };

  const onSubmit = (data: PersonalInformationFormData) => {
    if (hasUncommittedAddress) {
      toast.error(t(Resources.Components.Shared.AddressInput.UncommittedQuery));
      return;
    }
    const { hasOtherNationality: _formOnly, ...party } = data; // never part of PartyDto
    onSave({
      ...initialValues,
      ...party,
      addresses: upsertAddressByType(initialValues.addresses || [], {
        ...party.address,
        type: AddressType.Permanent,
      }),
      licenses: initialValues.licenses || [],
    });
  };

  return (
    <FormProvider {...formMethods}>
      <AddressUncommittedProvider value={uncommittedContextValue}>
        <FullHeightForm onSubmit={handleSubmit(onSubmit)}>
          <StepBox submitButtonLabel={t(Resources.Common.Save)}>
            <ValuesBox
              title={t(PERSONAL_RESOURCES.FormGroups.PersonalInformation)}
              hideDivider
            >
              <FormInput
                control={control}
                name="firstName"
                label={t(Resources.Common.FirstName)}
              />
              <FormInput
                control={control}
                name="lastName"
                label={t(Resources.Common.LastName)}
              />
              <FormInput
                control={control}
                name="personalNumber"
                label={t(Resources.Common.PersonalNumber)}
                mask="000000/0000"
              />
              <Dropdown
                codeList={genderList}
                control={control}
                name="gender"
                label={t(Resources.Common.Gender)}
              />
              <FormInput
                control={control}
                name="birthPlace"
                label={t(Resources.Common.BirthPlace)}
              />
              <CountryDropdown
                control={control}
                name="nationality"
                label={t(Resources.Common.Nationality)}
                onChange={handleNationalityChange}
              />
              <Box mb={2}>
                <CheckboxInput
                  control={control}
                  name="hasOtherNationality"
                  label={t(Resources.Common.OtherNationalities)}
                  disabled={!nationality}
                  onChange={handleOtherNationalityChange}
                />
              </Box>
              {hasOtherNationality && (
                <CountryDropdown
                  control={control}
                  name="otherNationalities.0"
                  accessibilityLabel={t(Resources.Common.OtherNationalities)}
                  excludedCode={nationality}
                />
              )}
            </ValuesBox>
            <Box mb={3}>
              <Typography
                fontSize={16}
                fontWeight={600}
                textTransform="uppercase"
              >
                {t(PERSONAL_RESOURCES.FormGroups.Address)}
              </Typography>
            </Box>
            <Stack gap={3}>
              {companyHeadquartersAddress && (
                <Button
                  variant="outlined"
                  startIcon={<HomeIcon />}
                  onClick={handleCopyCompanyAddress}
                  fullWidth
                >
                  {t(PERSONAL_RESOURCES.UseCompanyAddress)}
                </Button>
              )}
              <AddressInput
                address={address}
                onAcceptSuggestion={handleAcceptAddressSuggestion}
                onClear={handleClearAddress}
                hasErrors={!!errors.address}
              >
                <FormInput
                  control={control}
                  name="address.street"
                  label={t(Resources.Common.Address.Street)}
                />
                <Grid container spacing={3}>
                  <Grid size={{ xs: 6 }}>
                    <FormInput
                      control={control}
                      name="address.streetNumber"
                      label={t(Resources.Common.Address.StreetNumber)}
                    />
                  </Grid>
                  <Grid size={{ xs: 6 }}>
                    <FormInput
                      control={control}
                      name="address.orientationNumber"
                      label={t(Resources.Common.Address.OrientationNumber)}
                    />
                  </Grid>
                </Grid>
                <FormInput
                  control={control}
                  name="address.municipality"
                  label={t(Resources.Common.Address.Municipality)}
                />
                <FormInput
                  control={control}
                  name="address.postalCode"
                  label={t(Resources.Common.Address.PostalCode)}
                  mask="000 00"
                />
              </AddressInput>
            </Stack>
          </StepBox>
        </FullHeightForm>
      </AddressUncommittedProvider>
    </FormProvider>
  );
};
